import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Upload, Star, Trash2, X, ArrowLeft, Mic, Sparkles, Tag, Users, Trophy, Video, Megaphone, Plug, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { MarketAdmin } from "../components/market/MarketAdmin";
import { WaiverPodcastAdmin } from "../components/admin/WaiverPodcastAdmin";

export default function AdminPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("podcast");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/login", { replace: true });
  }, [user, loading, nav]);

  if (!user || user.role !== "admin") return null;

  const TABS = [
    { v: "podcast", l: "Podcast", icon: Mic },
    { v: "featured", l: "Featured", icon: Star },
    { v: "promos", l: "Promo Codes", icon: Tag },
    { v: "users", l: "Users & Plans", icon: Users },
    { v: "affiliates", l: "Affiliates", icon: Trophy },
    { v: "sessions", l: "Sessions", icon: Video },
    { v: "banner", l: "Site Banner", icon: Megaphone },
    { v: "integrations", l: "Integrations", icon: Plug },
    { v: "market", l: "Consignment", icon: Tag },
    { v: "waivers", l: "Waivers & Guests", icon: Mic },
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)] py-12 px-6 md:px-12">
      <div className="container-wide">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/" className="text-sm text-[var(--muted)] flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</Link>
          <span className="text-[var(--muted)]">·</span>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)]">Admin Console</p>
        </div>
        <h1 className="font-serif font-light text-5xl mb-2">Mr. & Mrs. Don Studio</h1>
        <p className="text-[var(--muted)] mb-10">Manage everything: episodes, featured couples, promos, users, affiliates, sessions.</p>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-[var(--line)] pb-4">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`admin-tab-${t.v}`} className={`text-xs uppercase tracking-[0.16em] px-4 py-2 rounded-full transition flex items-center gap-2 ${tab === t.v ? "bg-[var(--ink)] text-white" : "text-[var(--muted)] hover:text-[var(--gold)]"}`}><t.icon className="w-3 h-3" /> {t.l}</button>
          ))}
        </div>

        {tab === "podcast" && <PodcastAdmin />}
        {tab === "featured" && <FeaturedCoupleAdmin />}
        {tab === "promos" && <PromoAdmin />}
        {tab === "users" && <UsersAdmin />}
        {tab === "affiliates" && <AffiliatesAdmin />}
        {tab === "sessions" && <SessionsAdmin />}
        {tab === "banner" && <BannerAdmin />}
        {tab === "integrations" && <IntegrationsAdmin />}
        {tab === "market" && <MarketAdmin />}
        {tab === "waivers" && <WaiverPodcastAdmin />}
      </div>
    </div>
  );
}

function PodcastAdmin() {
  const [episodes, setEpisodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", topic: "Marriage Advice", duration_min: 30, audio_url: "", video_url: "", thumbnail_url: "" });
  const fileRef = useRef();

  async function load() { const { data } = await api.get("/podcast/episodes"); setEpisodes(data); }
  useEffect(() => { load().catch(() => {}); }, []);

  async function uploadMedia(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/podcast/upload-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND_URL}${data.url}`;
      const isVideo = file.type.startsWith("video/");
      setForm((f) => ({ ...f, [isVideo ? "video_url" : "audio_url"]: url }));
      toast.success(`${isVideo ? "Video" : "Audio"} uploaded`);
    } catch (e) { toast.error(formatApiError(e)); } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function add(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/podcast/episodes", form);
      setForm({ title: "", description: "", topic: "Marriage Advice", duration_min: 30, audio_url: "", video_url: "", thumbnail_url: "" });
      setOpen(false);
      toast.success("Episode published");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function del(id) {
    if (!confirm("Delete episode?")) return;
    await api.delete(`/admin/podcast/episodes/${id}`);
    toast.success("Deleted");
    load();
  }

  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Locked In Lessons</p>
          <h2 className="font-serif text-3xl">Episode Manager</h2>
        </div>
        <button onClick={() => setOpen(true)} data-testid="admin-new-episode" className="btn-gold"><Plus className="w-4 h-4" /> New Episode</button>
      </div>

      <div className="space-y-3">
        {episodes.map((ep) => (
          <div key={ep.episode_id} className="flex items-center gap-3 py-3 border-b border-[var(--line)] last:border-0">
            <div className="w-12 h-12 rounded-lg bg-[var(--bg-warm)] flex items-center justify-center"><Mic className="w-5 h-5 text-[var(--gold)]" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg truncate">{ep.title}</p>
              <p className="text-xs text-[var(--muted)]">Ep {ep.episode_number} · {ep.duration_min} min · {ep.topic}</p>
            </div>
            <button onClick={() => del(ep.episode_id)}><Trash2 className="w-4 h-4 text-[var(--muted)] hover:text-red-600" /></button>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6"><h3 className="font-serif text-2xl">New Episode</h3><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <form onSubmit={add} className="space-y-4">
              <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Title</label><input required data-testid="ep-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="input-luxe" /></div>
              <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Description</label><textarea required rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-luxe resize-none" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Topic</label><input value={form.topic} onChange={(e) => setForm({...form, topic: e.target.value})} className="input-luxe" /></div>
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Duration (min)</label><input type="number" value={form.duration_min} onChange={(e) => setForm({...form, duration_min: parseInt(e.target.value)||0})} className="input-luxe" /></div>
              </div>

              <div className="bg-[var(--bg-warm)] rounded-xl p-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-3">Media (audio or video)</p>
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-outline-gold w-full"><Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload audio/video file"}</button>
                <input ref={fileRef} type="file" accept="audio/*,video/*" onChange={uploadMedia} className="hidden" />
                {form.audio_url && <p className="text-xs text-[var(--gold)] mt-2 truncate">🎵 Audio: {form.audio_url.split("/").pop()}</p>}
                {form.video_url && <p className="text-xs text-[var(--gold)] mt-2 truncate">🎬 Video: {form.video_url.split("/").pop()}</p>}
                <p className="text-[11px] text-[var(--muted)] mt-3">Or paste an external URL:</p>
                <input value={form.audio_url} onChange={(e) => setForm({...form, audio_url: e.target.value})} placeholder="Audio URL" className="input-luxe" />
                <input value={form.video_url} onChange={(e) => setForm({...form, video_url: e.target.value})} placeholder="Video URL (YouTube embed, etc)" className="input-luxe" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Thumbnail URL</label>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!form.title) { toast.error("Add a title first"); return; }
                      setBusy(true);
                      try {
                        const { data } = await api.post("/podcast/cover/generate", {
                          title: form.title,
                          topic: form.topic,
                          episode_number: episodes.length + 1,
                        });
                        setForm((f) => ({ ...f, thumbnail_url: `${BACKEND_URL}${data.url}` }));
                        toast.success("Cover generated");
                      } catch (e) { toast.error(formatApiError(e)); }
                      finally { setBusy(false); }
                    }}
                    disabled={busy}
                    data-testid="generate-cover-btn"
                    className="text-[11px] text-[var(--gold)] hover:underline flex items-center gap-1"
                  >
                    <Sparkles className="w-3 h-3" /> Generate AI cover
                  </button>
                </div>
                <input value={form.thumbnail_url} onChange={(e) => setForm({...form, thumbnail_url: e.target.value})} className="input-luxe" />
                {form.thumbnail_url && <img src={form.thumbnail_url} alt="" className="w-32 h-32 object-cover rounded-xl mt-2" />}
              </div>

              <button type="submit" disabled={busy} className="btn-gold w-full">{busy ? "Publishing…" : "Publish Episode"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FeaturedCoupleAdmin() {
  const [board, setBoard] = useState([]);
  const [story, setStory] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [picking, setPicking] = useState(null);
  const [current, setCurrent] = useState(null);

  async function load() {
    const { data } = await api.get("/featured-couple/leaderboard");
    setBoard(data);
    const f = await api.get("/featured-couple");
    setCurrent(f.data.featured);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  async function pick(weddingId) {
    setPicking(weddingId);
    try {
      await api.post("/featured-couple/pick", { wedding_id: weddingId, story, photo_url: photoUrl });
      toast.success("Featured pick set for this week");
      setStory("");
      setPhotoUrl("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setPicking(null); }
  }

  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Featured Couple</p>
        <h2 className="font-serif text-3xl">Couple of the Week</h2>
      </div>

      {current && (
        <div className="bg-[var(--bg-warm)] rounded-xl p-4 mb-6 flex items-center gap-3">
          <Star className="w-5 h-5 text-[var(--gold)] shrink-0" />
          <div className="flex-1">
            <p className="font-serif text-xl">{current.wedding.couple_names}</p>
            <p className="text-xs text-[var(--muted)]">{current.auto ? "Auto-selected (highest score)" : "Admin pick"}</p>
          </div>
        </div>
      )}

      <div className="space-y-3 mb-6 bg-[var(--bg-warm)] rounded-xl p-4">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Optional override copy</p>
        <textarea rows={2} value={story} onChange={(e) => setStory(e.target.value)} placeholder="Custom feature story (optional)…" className="input-luxe resize-none" />
        <input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="Custom photo URL (optional)" className="input-luxe" />
      </div>

      <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-3">Engagement Leaderboard</p>
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {board.map((row, i) => (
          <div key={row.wedding_id} className="flex items-center gap-3 py-2 border-b border-[var(--line)] last:border-0">
            <span className="font-serif text-2xl w-8 text-[var(--muted)]">{i+1}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{row.couple_names || "—"}</p>
              <p className="text-xs text-[var(--muted)]">/site/{row.slug} · score {row.score}</p>
            </div>
            <button data-testid={`pick-${row.wedding_id}`} onClick={() => pick(row.wedding_id)} disabled={picking === row.wedding_id} className="text-xs px-3 py-1.5 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition">{picking === row.wedding_id ? "Picking…" : "Pick"}</button>
          </div>
        ))}
        {board.length === 0 && <p className="text-sm text-[var(--muted)] py-6 text-center">No couples yet.</p>}
      </div>
    </div>
  );
}

function PromoAdmin() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ code: "", type: "percent", value: 20, max_uses: 100, valid_until: "", applies_to_plans: ["premium", "luxury"], description: "" });

  async function load() { const { data } = await api.get("/admin/promo-codes"); setList(data); }
  useEffect(() => { load().catch(() => {}); }, []);

  async function create(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/promo-codes", form);
      setOpen(false); setForm({ code: "", type: "percent", value: 20, max_uses: 100, valid_until: "", applies_to_plans: ["premium", "luxury"], description: "" });
      toast.success("Promo created");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function del(id) { if (!confirm("Delete?")) return; await api.delete(`/admin/promo-codes/${id}`); load(); }

  function togglePlan(p) {
    setForm((f) => ({ ...f, applies_to_plans: f.applies_to_plans.includes(p) ? f.applies_to_plans.filter(x => x !== p) : [...f.applies_to_plans, p] }));
  }

  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
      <div className="flex justify-between items-center mb-6">
        <div><p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Promotions</p><h2 className="font-serif text-3xl">Promo Codes</h2></div>
        <button onClick={() => setOpen(true)} className="btn-gold" data-testid="admin-new-promo"><Plus className="w-4 h-4" /> New Code</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]"><tr><th className="px-3 py-2">Code</th><th className="px-3 py-2">Discount</th><th className="px-3 py-2">Used</th><th className="px-3 py-2">Plans</th><th className="px-3 py-2">Until</th><th></th></tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.promo_id} className="border-t border-[var(--line)]">
                <td className="px-3 py-3 font-mono font-medium">{p.code}</td>
                <td className="px-3 py-3">{p.type === "percent" ? `${p.value}%` : `$${p.value}`}</td>
                <td className="px-3 py-3 text-[var(--muted)]">{p.used_count}/{p.max_uses}</td>
                <td className="px-3 py-3 text-xs text-[var(--muted)]">{p.applies_to_plans.join(", ")}</td>
                <td className="px-3 py-3 text-[var(--muted)]">{p.valid_until || "—"}</td>
                <td className="px-3 py-3 text-right"><button onClick={() => del(p.promo_id)}><Trash2 className="w-4 h-4 text-[var(--muted)] hover:text-red-600" /></button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="6" className="px-3 py-12 text-center text-[var(--muted)]">No promo codes yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg">
            <div className="flex justify-between mb-6"><h3 className="font-serif text-2xl">New Promo Code</h3><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <form onSubmit={create} className="space-y-4">
              <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Code</label><input data-testid="promo-code" required value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} className="input-luxe font-mono" placeholder="LOVE20" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Type</label><select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="input-luxe"><option value="percent">% off</option><option value="fixed">$ off</option></select></div>
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Value</label><input required type="number" value={form.value} onChange={(e) => setForm({...form, value: parseFloat(e.target.value)||0})} className="input-luxe" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Max uses</label><input type="number" value={form.max_uses} onChange={(e) => setForm({...form, max_uses: parseInt(e.target.value)||1})} className="input-luxe" /></div>
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Valid until</label><input type="date" value={form.valid_until} onChange={(e) => setForm({...form, valid_until: e.target.value})} className="input-luxe" /></div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] block mb-2">Applies to</label>
                <div className="flex gap-2">{["premium", "luxury", "pro"].map(p => <button key={p} type="button" onClick={() => togglePlan(p)} className={`text-xs px-3 py-1.5 rounded-full ${form.applies_to_plans.includes(p) ? "bg-[var(--ink)] text-white" : "border border-[var(--line)]"}`}>{p}</button>)}</div>
              </div>
              <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Description</label><input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-luxe" /></div>
              <button type="submit" disabled={busy} data-testid="promo-submit" className="btn-gold w-full">{busy ? "Creating…" : "Create"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  async function load() { const { data } = await api.get("/admin/users"); setUsers(data); }
  useEffect(() => { load().catch(() => {}); }, []);

  async function setPlan(uid, plan) {
    await api.patch("/admin/users/plan", { user_id: uid, plan });
    toast.success(`Plan updated to ${plan}`);
    load();
  }

  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
      <div className="mb-6"><p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Subscriptions</p><h2 className="font-serif text-3xl">Users & Plans</h2></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]"><tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Plan</th><th className="px-3 py-2">Points</th><th className="px-3 py-2">Joined</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-[var(--line)]">
                <td className="px-3 py-3 font-medium">{u.name}</td>
                <td className="px-3 py-3 text-[var(--muted)]">{u.email}</td>
                <td className="px-3 py-3 capitalize">{u.role}</td>
                <td className="px-3 py-3"><select data-testid={`plan-${u.user_id}`} value={u.plan} onChange={(e) => setPlan(u.user_id, e.target.value)} className="text-xs px-2 py-1 rounded-full bg-[var(--bg-warm)] outline-none">{["free","premium","luxury","pro"].map(p => <option key={p}>{p}</option>)}</select></td>
                <td className="px-3 py-3">{u.points_balance || 0}</td>
                <td className="px-3 py-3 text-[var(--muted)] text-xs">{u.created_at?.slice(0,10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AffiliatesAdmin() {
  const [board, setBoard] = useState([]);
  useEffect(() => { api.get("/admin/affiliates/leaderboard").then((r) => setBoard(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
      <div className="mb-6"><p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Multi-Level Marketing</p><h2 className="font-serif text-3xl">Affiliate Leaderboard</h2></div>
      {board.length === 0 ? <p className="text-sm text-[var(--muted)] py-6 text-center">No commissions earned yet.</p> : (
        <div className="space-y-2">
          {board.map((row, i) => (
            <div key={row.user_id} className="flex items-center gap-4 py-3 border-b border-[var(--line)] last:border-0">
              <span className="font-serif text-2xl w-10 text-[var(--muted)]">{i+1}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{row.name}</p>
                <p className="text-xs text-[var(--muted)] truncate">{row.email} · code {row.referral_code}</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-[var(--gold)]">${row.lifetime_cash.toFixed(2)}</p>
                <p className="text-xs text-[var(--muted)]">{row.lifetime_points} pts · {row.txn_count} txns</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SessionsAdmin() {
  const [sessions, setSessions] = useState([]);
  async function load() { const { data } = await api.get("/admin/sessions"); setSessions(data); }
  useEffect(() => { load().catch(() => {}); }, []);
  async function setStatus(id, status) {
    await api.patch(`/admin/sessions/${id}`, { status });
    toast.success(`Set to ${status}`);
    load();
  }
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
      <div className="mb-6"><p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Live & Therapy</p><h2 className="font-serif text-3xl">Booked Sessions</h2></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]"><tr><th className="px-3 py-2">When</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">User</th><th className="px-3 py-2">Topic</th><th className="px-3 py-2">Status</th><th></th></tr></thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.session_id} className="border-t border-[var(--line)]">
                <td className="px-3 py-3 text-xs">{new Date(s.scheduled_at).toLocaleString()}</td>
                <td className="px-3 py-3 text-xs">{s.type.replace(/_/g, " ")}</td>
                <td className="px-3 py-3 text-xs"><div>{s.user?.name || "—"}</div><div className="text-[var(--muted)]">{s.user?.email}</div></td>
                <td className="px-3 py-3 text-xs">{s.topic || "—"}</td>
                <td className="px-3 py-3"><select value={s.status} onChange={(e) => setStatus(s.session_id, e.target.value)} className="text-xs px-2 py-1 rounded-full bg-[var(--bg-warm)] outline-none">{["pending","confirmed","completed","cancelled"].map(p => <option key={p}>{p}</option>)}</select></td>
                <td className="px-3 py-3 text-right"><a href={`/session/${s.session_id}`} target="_blank" rel="noreferrer" className="text-xs text-[var(--gold)]">Join</a></td>
              </tr>
            ))}
            {sessions.length === 0 && <tr><td colSpan="6" className="px-3 py-12 text-center text-[var(--muted)]">No bookings yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function BannerAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    text: "",
    cta_label: "",
    cta_url: "",
    bg_color: "#111111",
    text_color: "#C8A96B",
    show_logo: true,
    is_active: true,
  });
  const [busy, setBusy] = useState(false);

  function load() {
    api.get("/admin/banner").then((r) => setItems(r.data || [])).catch(() => {});
  }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    if (!form.text.trim()) return toast.error("Banner text is required");
    setBusy(true);
    try {
      await api.post("/admin/banner", form);
      toast.success("Banner published");
      setForm({ ...form, text: "", cta_label: "", cta_url: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  }

  async function remove(id) {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await api.delete(`/admin/banner/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  const active = items.find((b) => b.is_active);

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-2xl border border-[var(--line)] p-6">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-2">Live Preview</p>
        {active ? (
          <div className="rounded-xl overflow-hidden">
            <div
              className="w-full text-center text-[13px] py-2.5 flex items-center justify-center gap-3"
              style={{ background: active.bg_color, color: active.text_color }}
            >
              {active.show_logo !== false && (
                <img src={`${BACKEND_URL}/api/brand/logo.png`} alt="" className="h-7 w-auto" />
              )}
              <span className="font-medium">{active.text}</span>
              {active.cta_label && active.cta_url && (
                <a className="underline underline-offset-4 ml-1 font-semibold" href={active.cta_url}>
                  {active.cta_label} →
                </a>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-[var(--muted)]">No active banner. Publish one below.</p>
        )}
      </div>

      <form onSubmit={save} className="bg-white rounded-2xl border border-[var(--line)] p-6 space-y-4">
        <p className="font-serif text-2xl">Publish a new banner</p>
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Text</label>
          <input data-testid="banner-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="✨ Limited-time: 30% off Premium with code FOREVER30" className="w-full border border-[var(--line)] rounded-xl px-4 py-2 mt-1" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">CTA label (optional)</label>
            <input data-testid="banner-cta-label" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="Claim Offer" className="w-full border border-[var(--line)] rounded-xl px-4 py-2 mt-1" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">CTA URL (optional)</label>
            <input data-testid="banner-cta-url" value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} placeholder="/pricing" className="w-full border border-[var(--line)] rounded-xl px-4 py-2 mt-1" />
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Background color</label>
            <input type="color" value={form.bg_color} onChange={(e) => setForm({ ...form, bg_color: e.target.value })} className="w-full h-10 mt-1 rounded-xl border border-[var(--line)]" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Text color</label>
            <input type="color" value={form.text_color} onChange={(e) => setForm({ ...form, text_color: e.target.value })} className="w-full h-10 mt-1 rounded-xl border border-[var(--line)]" />
          </div>
          <label className="flex items-center gap-2 mt-7">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            <span className="text-sm">Activate immediately</span>
          </label>
        </div>
        <label className="flex items-center gap-2">
          <input data-testid="banner-show-logo" type="checkbox" checked={form.show_logo} onChange={(e) => setForm({ ...form, show_logo: e.target.checked })} />
          <span className="text-sm">Show official logo on banner</span>
        </label>
        <button disabled={busy} type="submit" data-testid="banner-publish" className="btn-gold">{busy ? "Publishing…" : "Publish Banner"}</button>
      </form>

      <div className="bg-white rounded-2xl border border-[var(--line)] p-6">
        <p className="font-serif text-2xl mb-4">History</p>
        <div className="space-y-3">
          {items.length === 0 && <p className="text-sm text-[var(--muted)]">No banners yet.</p>}
          {items.map((b) => (
            <div key={b.banner_id} className="flex items-center gap-3 p-3 rounded-xl border border-[var(--line)]">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: b.is_active ? "#16a34a" : "#9ca3af" }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{b.text}</p>
                <p className="text-[11px] text-[var(--muted)]">{new Date(b.updated_at).toLocaleString()}</p>
              </div>
              <button onClick={() => remove(b.banner_id)} className="text-[var(--muted)] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function IntegrationsAdmin() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [testResult, setTestResult] = useState({});
  const [showWizard, setShowWizard] = useState(false);

  async function load() {
    const { data } = await api.get("/admin/integrations");
    setItems(data);
    // Show first-run wizard if NOTHING is configured yet
    const anyConfigured = data.some((it) => it.configured);
    const dismissed = localStorage.getItem("integrations-wizard-dismissed");
    if (!anyConfigured && !dismissed) setShowWizard(true);
    // Seed drafts with empty fields so secrets aren't pre-filled with masked text
    const ds = {};
    data.forEach((it) => {
      ds[it.provider] = { config: {}, is_active: it.is_active };
    });
    setDrafts(ds);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  function setField(provider, key, value) {
    setDrafts((d) => ({
      ...d,
      [provider]: {
        ...d[provider],
        config: { ...(d[provider]?.config || {}), [key]: value },
      },
    }));
  }
  function setActive(provider, val) {
    setDrafts((d) => ({ ...d, [provider]: { ...d[provider], is_active: val } }));
  }

  async function save(provider) {
    setBusy(provider);
    try {
      await api.put(`/admin/integrations/${provider}`, drafts[provider] || {});
      toast.success("Saved");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  }
  async function test(provider) {
    setBusy(`test-${provider}`);
    setTestResult((t) => ({ ...t, [provider]: null }));
    try {
      const { data } = await api.post(`/admin/integrations/${provider}/test`);
      setTestResult((t) => ({ ...t, [provider]: data }));
    } catch (err) {
      setTestResult((t) => ({ ...t, [provider]: { ok: false, message: formatApiError(err) } }));
    } finally { setBusy(null); }
  }
  async function clear(provider) {
    if (!window.confirm(`Remove all saved ${provider} credentials?`)) return;
    await api.delete(`/admin/integrations/${provider}`);
    setTestResult((t) => ({ ...t, [provider]: null }));
    toast.success("Cleared");
    load();
  }

  return (
    <div className="space-y-5" data-testid="integrations-admin">
      {showWizard && <IntegrationsWizard start={typeof showWizard === "number" ? showWizard - 1 : 0} onClose={() => { setShowWizard(false); localStorage.setItem("integrations-wizard-dismissed", "1"); load(); }} />}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-sm text-[var(--muted)] max-w-2xl">
          Paste live API keys for your service providers. Secrets are stored encrypted in the database (server-side) and only previewed as masked values. Toggle <strong>Active</strong> to start using a provider; the platform falls back to mocked behavior when inactive.
        </p>
        <button
          onClick={() => setShowWizard(true)}
          data-testid="open-wizard-btn"
          className="btn-outline-gold text-xs"
        >
          <Sparkles className="w-3 h-3" /> First-run wizard
        </button>
      </div>
      {items.map((it) => {
        const draft = drafts[it.provider] || { config: {}, is_active: it.is_active };
        const result = testResult[it.provider];
        return (
          <div key={it.provider} className="bg-white border border-[var(--line)] rounded-2xl p-6" data-testid={`integration-${it.provider}`}>
            <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${it.is_active ? "bg-green-100 text-green-700" : "bg-[var(--bg-warm)] text-[var(--muted)]"}`}>
                  {it.is_active ? <Check className="w-5 h-5" /> : <Plug className="w-5 h-5" />}
                </div>
                <div>
                  <p className="font-serif text-xl">{it.label}</p>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">
                    {it.is_active ? "Active" : it.configured ? "Configured · Inactive" : "Not configured"}
                    {it.updated_at && ` · ${new Date(it.updated_at).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!draft.is_active}
                  onChange={(e) => setActive(it.provider, e.target.checked)}
                  data-testid={`active-${it.provider}`}
                  className="w-4 h-4 accent-[var(--gold)]"
                />
                Active
              </label>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {it.fields.map((f) => (
                <div key={f.key}>
                  <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">{f.label}</label>
                  <input
                    type={f.secret ? "password" : "text"}
                    placeholder={it.config?.[f.key] || f.placeholder || ""}
                    value={draft.config?.[f.key] ?? ""}
                    onChange={(e) => setField(it.provider, f.key, e.target.value)}
                    data-testid={`field-${it.provider}-${f.key}`}
                    className="input-luxe mt-1"
                  />
                  {f.secret && it.config?.[f.key] && (
                    <p className="text-[10px] text-[var(--muted)] mt-1 font-mono">Saved: {it.config[f.key]}</p>
                  )}
                </div>
              ))}
            </div>

            {result && (
              <div className={`mt-4 rounded-xl p-3 text-sm flex items-start gap-2 ${result.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`} data-testid={`test-result-${it.provider}`}>
                {result.ok ? <Check className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
                <span>{result.message}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-5">
              <button
                onClick={() => save(it.provider)}
                disabled={busy === it.provider}
                data-testid={`save-${it.provider}`}
                className="btn-gold text-xs"
              >
                {busy === it.provider ? "Saving…" : "Save credentials"}
              </button>
              <button
                onClick={() => test(it.provider)}
                disabled={busy === `test-${it.provider}` || !it.is_active}
                data-testid={`test-${it.provider}`}
                className="btn-outline-gold text-xs"
              >
                {busy === `test-${it.provider}` ? "Testing…" : "Test connection"}
              </button>
              {WIZARD_STEPS.findIndex((w) => w.provider === it.provider) >= 0 && (
                <button onClick={() => setShowWizard(WIZARD_STEPS.findIndex((w) => w.provider === it.provider) + 1)} data-testid={`guide-${it.provider}`} className="text-xs text-[var(--gold)] hover:underline px-3">
                  How to get this key →
                </button>
              )}
              {it.configured && (
                <button
                  onClick={() => clear(it.provider)}
                  data-testid={`clear-${it.provider}`}
                  className="text-xs text-[var(--muted)] hover:text-red-600 px-3"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


const WIZARD_STEPS = [
  {
    provider: "resend",
    title: "Resend · Transactional Email",
    tagline: "Send RSVP reminders, announcements, and couple digests.",
    signup_url: "https://resend.com/signup",
    key_url: "https://resend.com/api-keys",
    docs_url: "https://resend.com/docs/introduction",
    tier: "Free tier: 3,000 emails/month, 100/day.",
    instructions: [
      "Create a free Resend account at resend.com",
      "Verify your sending domain (needed to email anyone other than yourself), then use an address on it as From email",
      "Go to API Keys → Create API Key → copy it (starts with `re_`)",
      "Paste it below and toggle Active",
    ],
    fields: [
      { key: "api_key", label: "API Key", secret: true, placeholder: "re_..." },
      { key: "from_email", label: "From email (optional)", placeholder: "noreply@yourdomain.com" },
      { key: "from_name", label: "From name (optional)", placeholder: "Locked In Forever" },
    ],
  },
  {
    provider: "twilio",
    title: "Twilio · SMS Reminders",
    tagline: "Text guests who haven't RSVPed and drop cinematic day-of reminders.",
    signup_url: "https://www.twilio.com/try-twilio",
    key_url: "https://console.twilio.com/",
    docs_url: "https://www.twilio.com/docs/sms",
    tier: "Trial: ~$15 USD credit · verified recipient numbers only.",
    instructions: [
      "Sign up for a Twilio trial account",
      "Go to Console → Phone Numbers → buy or activate a number",
      "From Console dashboard copy Account SID and Auth Token",
      "Paste all three below and toggle Active",
    ],
    fields: [
      { key: "account_sid", label: "Account SID", secret: true, placeholder: "AC..." },
      { key: "auth_token", label: "Auth Token", secret: true },
      { key: "phone_number", label: "From phone number (E.164)", placeholder: "+15551234567" },
    ],
  },
  {
    provider: "textbelt",
    title: "Textbelt · Text Messages",
    tagline: "Send vendors their weekly cold-lead reminder by text.",
    signup_url: "https://textbelt.com/create-key/",
    key_url: "https://textbelt.com/create-key/",
    docs_url: "https://docs.textbelt.com/",
    tier: "Pay as you go · buy a pack of texts, no monthly fee.",
    instructions: [
      "Go to textbelt.com/create-key and buy a key with your email",
      "Copy the key from the confirmation email",
      "Paste it below — we check your remaining texts without sending one",
    ],
    fields: [{ key: "api_key", label: "API Key", secret: true, placeholder: "Your Textbelt key" }],
  },
  {
    provider: "elevenlabs",
    title: "ElevenLabs · Voice",
    tagline: "Read animated invites aloud and make spoken reception announcements.",
    signup_url: "https://elevenlabs.io/app/sign-up",
    key_url: "https://elevenlabs.io/app/settings/api-keys",
    docs_url: "https://elevenlabs.io/docs",
    tier: "Free tier: about 10,000 characters a month.",
    instructions: [
      "Create an ElevenLabs account",
      "Go to Developers → API keys → Create key (allow Text to Speech and Voices)",
      "Paste the full key below — we check it without using any credits",
    ],
    fields: [{ key: "api_key", label: "API Key", secret: true, placeholder: "Your ElevenLabs key" }],
  },
  {
    provider: "stripe_connect",
    title: "Stripe Connect · Vendor Payouts",
    tagline: "Let photographers, planners, and vendors accept bookings with direct payouts.",
    signup_url: "https://dashboard.stripe.com/settings/connect/platform-profile",
    key_url: "https://dashboard.stripe.com/settings/connect",
    docs_url: "https://stripe.com/docs/connect/express-accounts",
    tier: "Platform fee configurable · Stripe-hosted onboarding.",
    instructions: [
      "Log in to your Stripe Dashboard",
      "Go to Settings → Connect → Platform profile",
      "Enable Connect in Express mode (recommended)",
      "Copy your secret key from Developers → API keys",
      "Paste it below, set your platform fee % and toggle Active",
    ],
    fields: [
      { key: "secret_key", label: "Stripe secret key", secret: true, placeholder: "sk_live_... or sk_test_..." },
      { key: "account_type", label: "Account type", placeholder: "express" },
      { key: "platform_fee_percent", label: "Platform fee %", placeholder: "10" },
    ],
  },
  {
    provider: "google_places",
    title: "Google Places · Vendor research & photos",
    tagline: "Find real wedding businesses with hours, ratings and photos, and add their photos in one click.",
    signup_url: "https://console.cloud.google.com/projectcreate",
    key_url: "https://console.cloud.google.com/apis/credentials",
    docs_url: "https://developers.google.com/maps/documentation/places/web-service/op-overview",
    tier: "Pay as you go · Google gives a monthly free credit; billing must be on.",
    instructions: [
      "Open Google Cloud Console and create (or pick) a project",
      "Turn on billing for the project (Billing → Link a billing account)",
      "Go to APIs & Services → Library, search “Places API (New)” and click Enable",
      "Go to APIs & Services → Credentials → Create credentials → API key",
      "Click the new key → API restrictions → restrict it to “Places API (New)” → Save",
      "Paste the key below — we run one small test search",
    ],
    fields: [{ key: "api_key", label: "API Key", secret: true, placeholder: "AIza..." }],
  },
  {
    provider: "yelp",
    title: "Yelp Fusion · Vendor research",
    tagline: "Adds Yelp listings, ratings and review counts to vendor search.",
    signup_url: "https://www.yelp.com/developers/v3/manage_app",
    key_url: "https://www.yelp.com/developers/v3/manage_app",
    docs_url: "https://docs.developer.yelp.com/docs/fusion-intro",
    tier: "Free trial, then a paid Yelp Places plan.",
    instructions: [
      "Sign in to Yelp and open Yelp Developers → Create App",
      "Fill in the app name (e.g. Locked In Forever) and agree to the terms",
      "Copy the long “API Key” (not the Client ID)",
      "Paste it below — we run one small test search",
    ],
    fields: [{ key: "api_key", label: "API Key", secret: true, placeholder: "Your Yelp API key" }],
  },
  {
    provider: "telnyx",
    title: "Telnyx · Browser calling",
    tagline: "Agents call, hold, transfer and record from the browser; inbound calls ring the Call Center.",
    signup_url: "https://telnyx.com/sign-up",
    key_url: "https://portal.telnyx.com/#/app/api-keys",
    docs_url: "https://developers.telnyx.com/docs/voice/webrtc",
    tier: "Pay as you go · about $1/month per number plus per-minute calling.",
    instructions: [
      "Create a Telnyx account and add a payment method",
      "Numbers → Buy Numbers: buy a local number (this is your caller ID)",
      "Voice → SIP Connections → Add → choose “Credentials” type; copy its Connection ID",
      "Assign your number to that connection (Numbers → your number → Connection)",
      "On the connection, set the webhook URL to https://YOUR-SITE/api/sales/telnyx/webhook",
      "Account → API Keys → Create API Key; also copy the Public Key (Keys & Credentials)",
      "Paste everything below — we check the key and connection",
    ],
    fields: [
      { key: "api_key", label: "API Key", secret: true, placeholder: "KEY..." },
      { key: "connection_id", label: "Credential connection ID", placeholder: "1234567890" },
      { key: "from_number", label: "Caller ID number", placeholder: "+16025551234" },
      { key: "public_key", label: "Webhook public key", secret: true, placeholder: "Public key" },
      { key: "dialin_number", label: "Meeting dial-in number (optional)", placeholder: "+16025559876" },
    ],
  },
];

function IntegrationsWizard({ onClose, start = 0 }) {
  const [step, setStep] = useState(start);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const current = WIZARD_STEPS[step];

  function setField(k, v) {
    setDraft((d) => ({ ...d, [current.provider]: { ...(d[current.provider] || {}), [k]: v } }));
  }

  async function saveAndTest() {
    setBusy(true);
    setResult(null);
    try {
      const cfg = draft[current.provider] || {};
      const nonEmpty = Object.fromEntries(Object.entries(cfg).filter(([, v]) => v !== ""));
      await api.put(`/admin/integrations/${current.provider}`, { config: nonEmpty, is_active: true });
      const { data } = await api.post(`/admin/integrations/${current.provider}/test`);
      if (!data.ok) {
        await api.put(`/admin/integrations/${current.provider}`, { config: {}, is_active: false });
        data.message = `${data.message} — saved but switched off until the test passes.`;
      }
      setResult(data);
      if (data.ok) toast.success(data.message);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  function skip() {
    setResult(null);
    if (step === WIZARD_STEPS.length - 1) {
      onClose();
      toast.message("You can reopen the wizard anytime from the Integrations tab.");
    } else {
      setStep(step + 1);
    }
  }

  function nextStep() {
    setResult(null);
    if (step === WIZARD_STEPS.length - 1) {
      onClose();
      toast.success("Integrations wizard complete!");
    } else {
      setStep(step + 1);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="integrations-wizard">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[var(--line)] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[var(--gold)] text-white flex items-center justify-center text-xs font-semibold">
              {step + 1}/{WIZARD_STEPS.length}
            </div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">Setup Wizard</p>
          </div>
          <button onClick={onClose} data-testid="wizard-close" className="text-[var(--muted)] hover:text-black"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 md:p-8">
          <h2 className="font-serif text-3xl mb-1">{current.title}</h2>
          <p className="text-sm text-[var(--muted)] mb-3">{current.tagline}</p>
          <p className="text-[11px] text-[var(--gold)] uppercase tracking-[0.16em] mb-6">{current.tier}</p>

          <div className="bg-[var(--bg-warm)] rounded-2xl p-5 mb-6">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mb-3">How to get your credentials</p>
            <ol className="space-y-2 text-sm list-decimal list-inside">
              {current.instructions.map((t, i) => <li key={i}>{t}</li>)}
            </ol>
            <div className="flex flex-wrap gap-2 mt-4">
              <a href={current.signup_url} target="_blank" rel="noreferrer" data-testid={`wizard-signup-${current.provider}`} className="btn-gold text-xs">
                Open signup
              </a>
              <a href={current.key_url} target="_blank" rel="noreferrer" className="btn-outline-gold text-xs">Open dashboard</a>
              <a href={current.docs_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--muted)] hover:text-[var(--gold)] px-3 py-2">Docs →</a>
            </div>
          </div>

          <div className="space-y-3">
            {current.fields.map((f) => (
              <div key={f.key}>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">{f.label}</label>
                <input
                  type={f.secret ? "password" : "text"}
                  placeholder={f.placeholder}
                  value={(draft[current.provider] || {})[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  data-testid={`wizard-${current.provider}-${f.key}`}
                  className="input-luxe mt-1"
                />
              </div>
            ))}
          </div>

          {result && (
            <div className={`mt-5 rounded-xl p-3 text-sm flex items-start gap-2 ${result.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`} data-testid="wizard-result">
              {result.ok ? <Check className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
              <span>{result.message}</span>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[var(--line)] px-6 py-4 flex items-center justify-between gap-3">
          <button onClick={skip} data-testid="wizard-skip" className="text-xs text-[var(--muted)] hover:text-[var(--gold)] px-3 py-2">Skip for now</button>
          <div className="flex items-center gap-2">
            {!result?.ok && (
              <button onClick={saveAndTest} disabled={busy} data-testid="wizard-save-test" className="btn-outline-gold text-xs">
                {busy ? "Testing…" : "Save & Test"}
              </button>
            )}
            <button onClick={nextStep} data-testid="wizard-next" className="btn-gold text-xs">
              {step === WIZARD_STEPS.length - 1 ? "Finish" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

